import type { ReactNode } from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from "remotion";
import { fourierConfig as config } from "./config";
import { heartModel, type FourierModel } from "./model";
import { epicycles, toSvgPath, visibleTrail } from "./math/epicycles";
import { clamp01, ease, getTimeline, getTrailLayout, type FilmTiming } from "./timeline";
import { SplitFourierScene } from "./components/SplitFourierScene";
import { CircleCounter } from "./components/CircleCounter";
import { CircleDetailInset } from "./components/CircleDetailInset";
import "./style.css";

type FilmProps = {
  model: FourierModel;
  targetLabel: string;
  introText?: readonly [string, string];
  teaser?: string;
  centerFinal?: boolean;
  finalArtwork?: ReactNode;
  showCircleDetail?: boolean;
  colors?: { background: string; circle: string; vector: string; trail: string; text: string; muted: string };
  timing?: FilmTiming;
  skipIntroCopy?: boolean;
  flatStyle?: boolean;
  outroText?: readonly [string, string];
  artworkReveal?: { delay: number; duration: number };
  traceWidth?: number;
  finalScale?: number;
  outroTextDelay?: number;
};
export const FourierFilm = ({
  model,
  targetLabel,
  introText = config.text.intro,
  teaser = config.text.teaser,
  centerFinal = false,
  finalArtwork,
  showCircleDetail = false,
  colors = config.colors,
  timing = config.timing,
  skipIntroCopy = false,
  flatStyle = false,
  outroText = config.text.outro,
  artworkReveal = { delay: 1.3, duration: 0.65 },
  traceWidth,
  finalScale = config.layout.finish.scale,
  outroTextDelay,
}: FilmProps) => {
  const { stages, dc } = model;
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const scene = getTimeline(frame, fps, timing);
  const target = getTrailLayout(frame, fps, timing);
  const focus = ease(
    ((scene.kind === "outro" ? scene.localFrame / fps : 0) -
      timing.focusDelay) /
      timing.focusDuration,
  );
  target.scale += (finalScale - config.layout.finish.scale) * focus;
  if (centerFinal) {
    target.x -= model.boundsCenter.x * target.scale * focus;
    target.y -= model.boundsCenter.y * target.scale * focus;
  }
  const intro = scene.kind === "intro";
  const outro = scene.kind === "outro";
  const stage = stages[intro ? 1 : scene.index];
  const t = intro ? 0.58 + scene.progress * 0.28 : scene.progress;
  const chain = epicycles(stage.coefficients, dc, t);
  const tip = chain[chain.length - 1].tip;
  const fadeFrames = Math.max(1, Math.round(timing.crossfade * fps));
  const enter = scene.kind === "drawing" && scene.index === 0 && timing.intro === 0
    ? 1
    : ease(scene.localFrame / fadeFrames);
  const circleOpacity = outro
    ? 1 - ease(scene.localFrame / (fps * (flatStyle ? 0.28 : 0.45)))
    : intro
      ? 0.85
      : enter;
  const previous =
    scene.kind === "drawing" && scene.index > 0
      ? stages[scene.index - 1]
      : null;
  const points = intro
    ? stage.trail
        .slice(
          Math.floor((t - 0.12) * config.trailResolution),
          Math.floor(t * config.trailResolution) + 1,
        )
        .concat(tip)
    : visibleTrail(stage.trail, t, tip);
  const path = outro ? stage.fullPath : toSvgPath(points);
  const introFade = intro
    ? 1 - ease((scene.localFrame - fps * 1.65) / (fps * 0.35))
    : 0;
  const finishText = outro
    ? ease((scene.localFrame - fps * (outroTextDelay ?? (flatStyle ? 0.2 : 0.65))) / (fps * (flatStyle ? 0.28 : 0.55)))
    : 0;
  const scale = config.layout.scale;
  const artworkOpacity =
    finalArtwork && outro
      ? ease((scene.localFrame / fps - artworkReveal.delay) / artworkReveal.duration)
      : 0;

  return (
    <AbsoluteFill
      className="fourier-film"
      style={{
        backgroundColor: colors.background,
        color: colors.text,
      }}
    >
      {!flatStyle && <AbsoluteFill
        style={{
          background:
            "radial-gradient(ellipse at 46% 51%, rgba(24,58,77,0.20) 0%, transparent 62%)",
        }}
      />}
      <div
        style={{
          position: "absolute",
          left: 96,
          top: 187,
          fontFamily: "Arial, sans-serif",
          fontSize: 23,
          letterSpacing: 5.4,
          color: colors.muted,
        }}
      >
        LOOP NIBBLE
      </div>
      {intro && !skipIntroCopy && (
        <div
          style={{
            position: "absolute",
            left: 96,
            top: 304,
            fontSize: 84,
            lineHeight: 1.43,
            letterSpacing: -3,
            fontWeight: 700,
            opacity: introFade,
          }}
        >
          <span style={{ color: colors.circle }}>{introText[0]}</span>
          <br />
          {introText[1]}
        </div>
      )}
      {!intro && !outro && (
        <CircleCounter count={stage.count} opacity={enter} colors={colors} />
      )}
      {outro && (
        <CircleCounter
          count={stage.count}
          opacity={1 - ease(scene.localFrame / (fps * 0.4))}
          colors={colors}
        />
      )}
      {outro && (
        <div
          style={{
            position: "absolute",
            left: 0,
            width: "100%",
            textAlign: "center",
            top: 320,
            opacity: finishText,
            fontSize: 76,
            lineHeight: 1.4,
            fontWeight: 700,
            letterSpacing: -2,
          }}
        >
          {outroText[0]}
          <br />
          <span style={{ color: colors.trail }}>
            {outroText[1]}
          </span>
        </div>
      )}
      <svg
        width={width}
        height={height}
        viewBox="0 0 1080 1920"
        style={{ position: "absolute", inset: 0, overflow: "hidden" }}
      >
        <defs>
          <filter id="trail-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation={5 / target.scale} />
          </filter>
          <filter id="tip-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation={6 / scale} />
          </filter>
        </defs>
        <g opacity={intro ? introFade : 1}>
          <line
            x1={config.layout.dividerX}
            x2={config.layout.dividerX}
            y1={785}
            y2={1280}
            stroke={colors.muted}
            strokeOpacity={(flatStyle ? 0.46 : 0.18) * (outro ? circleOpacity : 1)}
          />
          <text
            x={config.layout.x}
            y={config.layout.labelY}
            textAnchor="middle"
            fontSize={34}
            fontWeight={700}
            fill={colors.circle}
            opacity={outro ? circleOpacity : 1}
          >
            {config.text.sourceLabel}
          </text>
          <text
            x={config.layout.targetX}
            y={config.layout.labelY}
            textAnchor="middle"
            fontSize={34}
            fontWeight={700}
            fill={colors.trail}
            opacity={outro ? circleOpacity : 1}
          >
            {targetLabel}
          </text>
          {previous && enter < 1 && (
            <SplitFourierScene
              chain={epicycles(previous.coefficients, dc, 1)}
              path={previous.fullPath}
              opacity={1 - enter}
              colors={colors}
              glow={flatStyle ? 0 : 0.25}
            />
          )}
          <SplitFourierScene
            chain={chain}
            opacity={1 - artworkOpacity}
            path={path}
            target={target}
            colors={colors}
            trailWidth={traceWidth ?? (flatStyle ? 7.6 : config.trailWidth)}
            circleOpacity={circleOpacity}
            tipOpacity={outro ? circleOpacity : intro ? 1 : enter}
            glow={
              flatStyle ? 0 : outro
                ? 0.34 +
                  0.05 *
                    Math.sin(clamp01(scene.localFrame / (fps * 3)) * Math.PI)
                : 0.25
            }
          />
          {showCircleDetail && !intro && !outro && (
            <CircleDetailInset chain={chain} y={1370} opacity={enter} />
          )}
          {finalArtwork && outro && (
            <g
              opacity={artworkOpacity}
              transform={`translate(${target.x} ${target.y}) scale(${target.scale})`}
            >
              {finalArtwork}
            </g>
          )}
        </g>
      </svg>
      {intro && !skipIntroCopy && (
        <div
          style={{
            position: "absolute",
            left: 96,
            top: 1520,
            fontSize: 32,
            letterSpacing: 2,
            color: colors.muted,
            opacity: introFade,
          }}
        >
          {teaser}
        </div>
      )}
    </AbsoluteFill>
  );
};

export const FourierHeart = () => (
  <FourierFilm model={heartModel} targetLabel={config.text.targetLabel} />
);

